import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router";

import { AppBreadcrumbs } from "@shell/components/AppBreadcrumbs";
import { getContainerStyles } from "@shell/styles/themeUtils";
import {
  ORGANIZATIONS,
  PLATFORM_PRODUCTS,
} from "src/platform/constants/routes";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";
import { useRegistryApi } from "src/platform/hooks/useRegistryApi";

// ── Product catalog ─────────────────────────────────────────────────────────
// Each product is discovered for the selected organization. A product is
// "Active" when an org-scoped service binding exists for it; otherwise it is
// "Available". Discovery and activation remain separate steps.

type ProductStatus =
  "Active" | "Available" | "Activating" | "Disabled" | "Planned";

type ProductMark = "gateway" | "identity";

interface ProductCatalogEntry {
  key: string;
  name: string;
  category: string;
  description: string;
  capabilities: string[];
  serviceAlias: string;
  mark: ProductMark;
  glyph: string;
  /** Footer note shown when the product is Active. */
  nextStepActive: string;
  /** Footer note shown when the product is Available (not yet activated). */
  nextStepAvailable: string;
}

const CATALOG_PRODUCTS: ProductCatalogEntry[] = [
  {
    key: "llm-gateway",
    name: "LLM Gateway",
    category: "AI infrastructure",
    description:
      "A managed, organization-scoped gateway for routing application requests to approved language-model backends.",
    capabilities: [
      "OpenAI-compatible API",
      "Usage visibility",
      "Scoped credentials",
    ],
    serviceAlias: "llm-gateway",
    mark: "gateway",
    glyph: "\u2726",
    nextStepActive: "Manage the active product workspace.",
    nextStepAvailable:
      "Activate the LLM Gateway to provision organization credentials.",
  },
  {
    key: "customer-identity",
    name: "Customer Identity",
    category: "Application identity",
    description:
      "A managed identity platform for customer applications — register OIDC/OAuth clients, manage user directories, and federate from external identity providers.",
    capabilities: ["User directories", "OIDC and OAuth clients", "Federation"],
    serviceAlias: "customer-identity",
    mark: "identity",
    glyph: "\u25CE",
    nextStepActive: "Manage customer identity clients and directories.",
    nextStepAvailable: "Activate and manage customer identity clients.",
  },
];

const BOUNDARY_INFO = [
  {
    title: "Organization-scoped",
    body: "Products are discovered in the context of the selected organization.",
  },
  {
    title: "Separate from Projects",
    body: "Rusha products are not customer projects, stacks, or deployed applications.",
  },
  {
    title: "Activation is explicit",
    body: "Seeing a product does not activate it or create credentials.",
  },
];

type StatusFilter = "all" | ProductStatus;

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All products" },
  { value: "Active", label: "Active" },
  { value: "Available", label: "Available" },
  { value: "Activating", label: "Activating" },
  { value: "Disabled", label: "Disabled" },
  { value: "Planned", label: "Planned" },
];

type LoadState = "loading" | "loaded" | "error" | "forbidden";

interface ServiceBinding {
  id: number;
  serviceAlias: string;
  status: string;
}

// ── Component ───────────────────────────────────────────────────────────────

const PlatformProductsPage: React.FC = () => {
  const theme = useTheme();
  const appNavigation = useAppNavigation();
  const { orgAlias } = useParams<{ orgAlias: string }>();

  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [bindings, setBindings] = useState<ServiceBinding[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const { call: listBindings } = useRegistryApi<ServiceBinding[]>(
    "application",
    "listOrgServiceBindings",
  );

  const loadCatalog = useCallback(async () => {
    if (!orgAlias) return;
    setLoadState("loading");
    try {
      const result = await listBindings({
        pathParams: { alias: orgAlias },
      });
      setBindings(result ?? []);
      setLoadState("loaded");
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      // 403 / permission failures surface a forbidden state; everything
      // else is a recoverable load error with a retry affordance.
      if (message.includes("403") || /forbidden/i.test(message)) {
        setLoadState("forbidden");
      } else {
        setLoadState("error");
      }
    }
  }, [orgAlias, listBindings]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  // Resolve each product's status from the binding list.
  const productStatuses = useMemo(() => {
    const map: Record<string, ProductStatus> = {};
    for (const product of CATALOG_PRODUCTS) {
      const binding = bindings.find(
        (b) => b.serviceAlias === product.serviceAlias,
      );
      if (binding?.status === "ready") {
        map[product.key] = "Active";
      } else if (
        binding?.status === "pending" ||
        binding?.status === "provisioning"
      ) {
        map[product.key] = "Activating";
      } else if (binding?.status === "disabled") {
        map[product.key] = "Disabled";
      } else {
        map[product.key] = "Available";
      }
    }
    return map;
  }, [bindings]);

  const visibleProducts = useMemo(() => {
    if (statusFilter === "all") return CATALOG_PRODUCTS;
    return CATALOG_PRODUCTS.filter(
      (p) => productStatuses[p.key] === statusFilter,
    );
  }, [statusFilter, productStatuses]);

  if (!orgAlias) {
    return null;
  }

  return (
    <Container sx={getContainerStyles(theme)}>
      <AppBreadcrumbs
        items={[
          { label: "Organizations", to: ORGANIZATIONS.LIST.path },
          {
            label: orgAlias,
            to: ORGANIZATIONS.DETAILS.getPath(orgAlias),
          },
          { label: PLATFORM_PRODUCTS.CATALOG.title },
        ]}
      />

      {loadState === "error" && (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void loadCatalog()}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Try again
            </Button>
          }
          sx={{ mb: 3 }}
        >
          Platform Products could not be loaded.
        </Alert>
      )}

      {loadState === "forbidden" && (
        <ForbiddenState
          onBack={() => appNavigation.goToOrganizationDetails(orgAlias)}
        />
      )}

      {loadState === "loading" && <CatalogSkeleton />}

      {loadState === "loaded" && (
        <Stack spacing={3}>
          <CatalogHeader orgAlias={orgAlias} />

          <Toolbar
            count={visibleProducts.length}
            filter={statusFilter}
            onFilterChange={setStatusFilter}
          />

          {visibleProducts.length === 0 ? (
            <EmptyState />
          ) : (
            <Grid container spacing={2}>
              {visibleProducts.map((product) => (
                <Grid item xs={12} md={6} key={product.key}>
                  <ProductCard
                    product={product}
                    status={productStatuses[product.key]}
                    onView={() =>
                      appNavigation.goToPlatformProductDetails(
                        orgAlias,
                        product.key,
                      )
                    }
                  />
                </Grid>
              ))}
            </Grid>
          )}

          <BoundaryGrid />
        </Stack>
      )}
    </Container>
  );
};

// ── Header ──────────────────────────────────────────────────────────────────

const CatalogHeader: React.FC<{ orgAlias: string }> = ({ orgAlias }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    justifyContent="space-between"
    alignItems={{ xs: "flex-start", sm: "flex-start" }}
    spacing={3}
  >
    <Stack spacing={1} sx={{ maxWidth: 680 }}>
      <Typography
        variant="overline"
        sx={{
          letterSpacing: "0.08em",
          fontWeight: 700,
          color: "text.secondary",
        }}
      >
        Rusha Platform Products
      </Typography>
      <Typography variant="h4" component="h1" fontWeight={700}>
        Products for your organization
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 1, maxWidth: 620 }}
      >
        Discover Rusha-managed services that can be activated for{" "}
        <strong>{orgAlias}</strong>. Active products are managed here after
        activation; discovery and activation remain separate steps.
      </Typography>
    </Stack>
    <OrgContextBadge orgAlias={orgAlias} />
  </Stack>
);

const OrgContextBadge: React.FC<{ orgAlias: string }> = ({ orgAlias }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    sx={{
      py: 1,
      px: 1.5,
      borderRadius: 1,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "background.paper",
      fontSize: 12,
      color: "text.secondary",
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}
  >
    <Box
      component="span"
      aria-hidden
      sx={{
        width: 7,
        height: 7,
        borderRadius: "50%",
        bgcolor: "success.main",
        boxShadow: `0 0 0 3px ${alpha("#10b981", 0.12)}`,
      }}
    />
    Organization: <strong>{orgAlias}</strong>
  </Stack>
);

// ── Toolbar ─────────────────────────────────────────────────────────────────

const Toolbar: React.FC<{
  count: number;
  filter: StatusFilter;
  onFilterChange: (value: StatusFilter) => void;
}> = ({ count, filter, onFilterChange }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    justifyContent="space-between"
    alignItems={{ xs: "stretch", sm: "center" }}
    spacing={2}
  >
    <Typography variant="body2" color="text.secondary">
      {count} {count === 1 ? "product" : "products"} in the catalog
    </Typography>
    <FormControl
      size="small"
      sx={{ minWidth: 220, width: { xs: "100%", sm: 220 } }}
    >
      <InputLabel id="product-status-filter-label">Filter products</InputLabel>
      <Select<StatusFilter>
        labelId="product-status-filter-label"
        id="product-status-filter"
        value={filter}
        label="Filter products"
        onChange={(e) => onFilterChange(e.target.value as StatusFilter)}
      >
        {STATUS_FILTER_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Stack>
);

// ── Product card ────────────────────────────────────────────────────────────

interface ProductCardProps {
  product: ProductCatalogEntry;
  status: ProductStatus;
  onView?: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  status,
  onView,
}) => {
  const theme = useTheme();
  const isActive = status === "Active";

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        minHeight: 268,
        display: "flex",
        flexDirection: "column",
        borderRadius: 1.5, // 12px
        // background.paper is translucent (0.72 alpha) and the base theme
        // applies backdrop-filter: blur(8px) to MuiPaper root, so the body
        // grid shows through the card. Do not override either locally.
        transition:
          "border-color 150ms ease, transform 150ms ease, background-color 150ms ease",
        "&:hover": {
          borderColor: "divider",
          backgroundColor: alpha(theme.palette.text.primary, 0.04),
          transform: "translateY(-1px)",
        },
      }}
    >
      <CardContent sx={{ p: 2.5, flexGrow: 1, "&:last-child": { pb: 2.5 } }}>
        <Stack spacing={2.25}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 0.5 }}
          >
            <ProductMark mark={product.mark} glyph={product.glyph} />
            <StatusChip status={status} />
          </Stack>
          <Stack spacing={0.25}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.3 }}>
              {product.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {product.category}
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.55, minHeight: 58 }}
          >
            {product.description}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {product.capabilities.map((capability) => (
              <Chip
                key={capability}
                label={capability}
                size="small"
                sx={{
                  height: "auto",
                  fontSize: 11,
                  padding: "2px 8px",
                  borderRadius: "6px",
                  backgroundColor: alpha(theme.palette.text.primary, 0.05),
                  border: "1px solid",
                  borderColor: "divider",
                  color: "text.secondary",
                  "& .MuiChip-label": { padding: "2px 0", fontWeight: 500 },
                }}
              />
            ))}
          </Stack>
        </Stack>
      </CardContent>
      <CardActions
        sx={{
          justifyContent: "space-between",
          px: 2.5,
          pb: 2.5,
          pt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="caption"
          sx={{ maxWidth: 205, lineHeight: 1.4, color: "text.secondary" }}
        >
          <strong>Next step:</strong>{" "}
          {isActive ? product.nextStepActive : product.nextStepAvailable}
        </Typography>
        <Button
          size="small"
          variant="contained"
          onClick={onView}
          sx={{ textTransform: "none", flexShrink: 0 }}
        >
          {isActive ? "Manage product" : "View product"}
        </Button>
      </CardActions>
    </Card>
  );
};

const ProductMark: React.FC<{ mark: ProductMark; glyph: string }> = ({
  mark,
  glyph,
}) => {
  const isIdentity = mark === "identity";
  return (
    <Box
      aria-hidden
      sx={{
        width: 44,
        height: 44,
        display: "grid",
        placeItems: "center",
        borderRadius: "12px",
        fontSize: 18,
        fontWeight: 700,
        color: isIdentity ? "#c4b5fd" : "#bfdbfe",
        backgroundColor: isIdentity
          ? "rgba(124, 58, 237, 0.18)"
          : "rgba(37, 99, 235, 0.2)",
        border: "1px solid",
        borderColor: isIdentity
          ? "rgba(167, 139, 250, 0.34)"
          : "rgba(96, 165, 250, 0.35)",
      }}
    >
      {glyph}
    </Box>
  );
};

const StatusChip: React.FC<{ status: ProductStatus }> = ({ status }) => {
  if (status === "Active") {
    return <Chip label="Active" color="success" size="small" />;
  }
  if (status === "Activating") {
    return (
      <Chip label="Activating" color="info" variant="outlined" size="small" />
    );
  }
  if (status === "Disabled") {
    return <Chip label="Disabled" color="default" size="small" />;
  }
  if (status === "Planned") {
    return (
      <Chip label="Planned" color="default" variant="outlined" size="small" />
    );
  }
  return (
    <Chip label="Available" color="info" variant="outlined" size="small" />
  );
};

// ── Boundary grid ───────────────────────────────────────────────────────────

const BoundaryGrid: React.FC = () => (
  <Grid container spacing={1.25} sx={{ mt: 1 }}>
    {BOUNDARY_INFO.map((item) => (
      <Grid item xs={12} sm={4} key={item.title}>
        <Box
          sx={{
            p: 1.75,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: alpha("#000", 0.02),
          }}
        >
          <Typography
            sx={{
              display: "block",
              mb: 0.5,
              fontSize: 12,
              fontWeight: 700,
              color: "text.primary",
            }}
            component="strong"
          >
            {item.title}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", lineHeight: 1.45, display: "block" }}
          >
            {item.body}
          </Typography>
        </Box>
      </Grid>
    ))}
  </Grid>
);

// ── States ──────────────────────────────────────────────────────────────────

const CatalogSkeleton: React.FC = () => (
  <Stack spacing={3}>
    <Stack spacing={1}>
      <Skeleton variant="text" width={160} height={16} />
      <Skeleton variant="text" width={280} height={32} />
      <Skeleton variant="text" width={520} height={20} />
    </Stack>
    <Grid container spacing={2}>
      {CATALOG_PRODUCTS.map((product) => (
        <Grid item xs={12} md={6} key={product.key}>
          <SkeletonCard />
        </Grid>
      ))}
    </Grid>
  </Stack>
);

const SkeletonCard: React.FC = () => (
  <Card
    variant="outlined"
    sx={{
      minHeight: 268,
      borderRadius: 1.5,
      backgroundColor: "background.paper",
      p: 2.5,
    }}
  >
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Skeleton variant="rounded" width={44} height={44} />
        <Skeleton variant="rounded" width={72} height={24} />
      </Stack>
      <Skeleton variant="text" width="60%" height={24} />
      <Skeleton variant="text" width="40%" height={16} />
      <Skeleton variant="text" width="100%" height={16} />
      <Skeleton variant="text" width="90%" height={16} />
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Skeleton variant="rounded" width={120} height={22} />
        <Skeleton variant="rounded" width={140} height={22} />
        <Skeleton variant="rounded" width={100} height={22} />
      </Stack>
    </Stack>
  </Card>
);

const EmptyState: React.FC = () => (
  <Box
    sx={{
      maxWidth: 480,
      mx: "auto",
      my: 6,
      p: 4,
      textAlign: "center",
      borderRadius: 1.5,
      border: "1px solid",
      borderColor: "divider",
      backgroundColor: "background.paper",
    }}
  >
    <Typography
      component="div"
      role="presentation"
      aria-hidden
      sx={{ fontSize: 40, color: "text.secondary", mb: 1 }}
    >
      {"\u25C7"}
    </Typography>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
      No products are available
    </Typography>
    <Typography variant="body2" color="text.secondary">
      There are no products to show for this organization yet.
    </Typography>
  </Box>
);

const ForbiddenState: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <Box
    sx={{
      maxWidth: 520,
      mx: "auto",
      my: 6,
      p: 4,
      textAlign: "center",
      borderRadius: 1.5,
      border: "1px solid",
      borderColor: "divider",
      backgroundColor: "background.paper",
    }}
  >
    <Typography
      component="div"
      role="presentation"
      aria-hidden
      sx={{ fontSize: 40, color: "text.secondary", mb: 1 }}
    >
      !
    </Typography>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
      Product catalog unavailable
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      You do not have permission to discover Rusha Platform Products for this
      organization.
    </Typography>
    <Button variant="outlined" onClick={onBack} sx={{ textTransform: "none" }}>
      Back to organization
    </Button>
  </Box>
);

export default PlatformProductsPage;
